import { useId, useState } from 'react';
import { MapIcon } from '../../icons';
import { tCalendar } from '../../lib/calendar-i18n';
import { EventTravelTime } from './EventTravelTime';
import { EventLocationMap } from './EventLocationMap';

/**
 * 「외부」 일정 상세의 장소 줄 — 글자 오른쪽 「지도」 단추로 이동 시간·지도를 펴고 접는다
 * (2026-10-30 · 사용자가 시안 B 로 고름: 「클릭해서 필요한 사람만 보이게」).
 *
 * ## 누르기 전에는 아무것도 부르지 않는다
 *
 * 종전에는 상세를 열자마자 `EventTravelTime` 이 이동 시간 API 와 카카오 지도를 불렀다. 여기서는
 * 단추를 «처음» 누를 때 그것을 마운트한다. 한 번 연 뒤에는 접어도 지우지 않고 `hidden` 으로 숨기기만 해
 * 다시 펴도 다시 부르지 않는다. 상세를 닫으면 이 부품도 사라지므로 다음에 열 때는 다시 닫힘이다.
 *
 * ## 보여 줄 것이 없을 때
 *
 * 이동 시간도 못 세고(`EventTravelTime` 이 fallback 으로 물러섬) 장소 지도도 못 찾으면 펼친 자리가
 * 비므로, 한 줄 안내를 둔다. 묻는 동안에는 그리지 않는다(두 조각이 다 답한 뒤에만).
 */
export function EventMapToggle({ eventId, occurrenceAt, location }: { eventId: string; occurrenceAt?: string | null; location: string }) {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [travelShown, setTravelShown] = useState<boolean | null>(null);
  const [placeFound, setPlaceFound] = useState<boolean | null>(null);
  const empty = travelShown === false && placeFound === false;

  const toggle = () => {
    setMounted(true);
    setOpen((v) => !v);
  };

  return (
    <div className="calendar-detail-location">
      <div className="calendar-detail-location-head">
        <span className="calendar-detail-location-text">{location}</span>
        <button
          type="button"
          className="calendar-detail-map-toggle"
          aria-expanded={open}
          aria-controls={mounted ? panelId : undefined}
          onClick={toggle}
          data-testid="detail-map-toggle"
        >
          <MapIcon size={14} aria-hidden />
          {tCalendar('calendar.detail.map_toggle')}
        </button>
      </div>
      {mounted && (
        <div id={panelId} className="calendar-detail-map-panel" hidden={!open} data-testid="detail-map-panel">
          <EventTravelTime
            eventId={eventId}
            occurrenceAt={occurrenceAt}
            onSettled={setTravelShown}
            fallback={<EventLocationMap location={location} onSettled={setPlaceFound} />}
          />
          {empty && (
            <div className="calendar-detail-map-empty" role="status">
              {tCalendar('calendar.detail.map_not_found')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
